/**
 * Generic Broker Parser
 * Config-driven parser for tradebook files from brokers that require ISIN lookup
 */

import * as XLSX from 'xlsx';
import { BrokerParserConfig, ParseResult, ParsedPosition, ParsedTranche, RejectedRow, RawTrade } from './types';
import { resolveBrokerSymbols } from '../../services/api';

/**
 * Parse a tradebook file using the provided broker config
 */
export async function parseWithConfig(
  file: File,
  config: BrokerParserConfig
): Promise<ParseResult> {
  console.log(`[GenericParser] Parsing ${file.name} for ${config.broker}`);
  
  // Step 1: Read file content (auto-detect format)
  const rows = await readFile(file);
  console.log(`[GenericParser] Read ${rows.length} rows`);
  
  if (rows.length < 2) {
    throw new Error('File is empty or has no data rows');
  }
  
  // Step 2: Find header row
  const headerRowIndex = findHeaderRow(rows, config);
  const headerRow = rows[headerRowIndex];
  console.log(`[GenericParser] Header at row ${headerRowIndex}:`, headerRow);
  
  // Step 3: Map column indices
  const columnMap = mapColumns(headerRow, config);
  console.log(`[GenericParser] Column mapping:`, columnMap);
  
  // Validate required columns
  validateColumns(columnMap, headerRow, config);
  
  // Step 4: Parse data rows into raw trades
  const { trades, rejectedRows } = parseRows(rows, headerRowIndex, columnMap, config);
  console.log(`[GenericParser] Parsed ${trades.length} trades, rejected ${rejectedRows.length} rows`);
  
  if (trades.length === 0) {
    return { positions: [], rejectedRows, unresolvedSymbols: [] };
  }
  
  // Step 5: Resolve ISINs via API
  const { resolvedTrades, unresolvedSymbols, lookupRejectedRows } = await resolveISINs(trades, config);
  console.log(`[GenericParser] Resolved ${resolvedTrades.length} trades, ${unresolvedSymbols.length} unresolved`);
  
  // Step 6: Aggregate by orderId (handle duplicate order rows)
  const aggregatedTrades = aggregateByOrderId(resolvedTrades);
  console.log(`[GenericParser] Aggregated to ${aggregatedTrades.length} unique orders`);
  
  // Step 7: Group by ISIN into positions
  const positions = groupIntoPositions(aggregatedTrades);
  console.log(`[GenericParser] Grouped into ${positions.length} positions`);
  
  return {
    positions,
    rejectedRows: [...rejectedRows, ...lookupRejectedRows],
    unresolvedSymbols,
  };
}

/**
 * Read file content - auto-detect Excel vs TSV vs CSV
 */
async function readFile(file: File): Promise<string[][]> {
  const arrayBuffer = await file.arrayBuffer();
  const data = new Uint8Array(arrayBuffer);
  
  // Check if it's actually text (TSV/CSV) by looking at first bytes
  const textDecoder = new TextDecoder('utf-8');
  const firstChunk = textDecoder.decode(data.slice(0, 100));
  const isText = /^[A-Za-z0-9,\t\r\n"'\-\/\s]/.test(firstChunk);
  
  if (isText && !isLikelyBinaryExcel(data)) {
    // It's a text file (TSV or CSV disguised as Excel)
    const text = textDecoder.decode(data);
    return parseTextFile(text);
  }
  
  // Try parsing as actual Excel
  try {
    const workbook = XLSX.read(data, { type: 'array' });
    const sheetName = workbook.SheetNames[0];
    const worksheet = workbook.Sheets[sheetName];
    const jsonData = XLSX.utils.sheet_to_json(worksheet, {
      header: 1,
      raw: false,
      defval: '',
    }) as string[][];
    return jsonData;
  } catch (xlsxError) {
    // If Excel parsing fails, try as text
    console.warn('[GenericParser] Excel parsing failed, trying as text:', xlsxError);
    const text = textDecoder.decode(data);
    return parseTextFile(text);
  }
}

/**
 * Check if data looks like binary Excel (has BIFF markers)
 */
function isLikelyBinaryExcel(data: Uint8Array): boolean {
  // Check for Excel BIFF magic bytes or ZIP (xlsx) magic bytes
  if (data.length < 4) return false;
  
  // XLSX (ZIP format)
  if (data[0] === 0x50 && data[1] === 0x4B) return true;
  
  // XLS (BIFF format) - starts with 0xD0 0xCF
  if (data[0] === 0xD0 && data[1] === 0xCF) return true;
  
  return false;
}

/**
 * Parse text file (TSV or CSV)
 */
function parseTextFile(text: string): string[][] {
  // Normalize line endings
  const normalizedText = text.replace(/\r\n/g, '\n').replace(/\r/g, '\n');
  const lines = normalizedText.trim().split('\n');
  
  // Detect delimiter (tab or comma)
  const firstLine = lines[0] || '';
  const tabCount = (firstLine.match(/\t/g) || []).length;
  const commaCount = (firstLine.match(/,/g) || []).length;
  const delimiter = tabCount > commaCount ? '\t' : ',';
  
  console.log(`[GenericParser] Detected delimiter: ${delimiter === '\t' ? 'TAB' : 'COMMA'}`);
  
  return lines.map(line => parseDelimitedLine(line, delimiter));
}

/**
 * Parse a single delimited line, handling quotes
 */
function parseDelimitedLine(line: string, delimiter: string): string[] {
  const values: string[] = [];
  let currentValue = '';
  let insideQuotes = false;
  
  for (let i = 0; i < line.length; i++) {
    const char = line[i];
    
    if (char === '"') {
      insideQuotes = !insideQuotes;
    } else if (char === delimiter && !insideQuotes) {
      values.push(currentValue.trim());
      currentValue = '';
    } else {
      currentValue += char;
    }
  }
  
  values.push(currentValue.trim());
  return values;
}

/**
 * Find the header row based on config
 */
function findHeaderRow(rows: string[][], config: BrokerParserConfig): number {
  if (config.headerConfig.rowIndex !== undefined) {
    return config.headerConfig.rowIndex;
  }
  
  if (config.headerConfig.searchPatterns) {
    for (let i = 0; i < Math.min(10, rows.length); i++) {
      const row = rows[i];
      const rowText = row.join(' ').toLowerCase();
      const found = config.headerConfig.searchPatterns.some(
        pattern => rowText.includes(pattern.toLowerCase())
      );
      if (found) {
        return i;
      }
    }
  }
  
  return 0;  // Default to first row
}

/**
 * Map column names to indices
 */
function mapColumns(headerRow: string[], config: BrokerParserConfig): Record<string, number> {
  const map: Record<string, number> = {};
  
  const normalizedHeader = headerRow.map(h => h.toLowerCase().trim());
  
  const findColumn = (columnName: string): number => {
    const normalized = columnName.toLowerCase().trim();
    const index = normalizedHeader.indexOf(normalized);
    if (index === -1) {
      // Try partial match
      const partialIndex = normalizedHeader.findIndex(h => h.includes(normalized) || normalized.includes(h));
      return partialIndex;
    }
    return index;
  };
  
  map.symbol = findColumn(config.columns.symbol);
  map.orderId = findColumn(config.columns.orderId);
  map.tradeType = findColumn(config.columns.tradeType);
  map.quantity = findColumn(config.columns.quantity);
  map.exchange = findColumn(config.columns.exchange);
  map.timestamp = findColumn(config.columns.timestamp);
  map.lookupColumn = findColumn(config.columns.isinLookup.lookupColumn);
  
  if (config.columns.price) {
    map.price = findColumn(config.columns.price);
  }
  if (config.columns.value) {
    map.value = findColumn(config.columns.value);
  }
  if (config.columns.orderStatus) {
    map.orderStatus = findColumn(config.columns.orderStatus);
  }
  
  return map;
}

/**
 * Validate required columns exist
 */
function validateColumns(columnMap: Record<string, number>, headerRow: string[], config: BrokerParserConfig): void {
  const requiredInternal = ['symbol', 'orderId', 'tradeType', 'quantity', 'exchange', 'lookupColumn'];
  const missingInternal = requiredInternal.filter(col => columnMap[col] === -1);
  
  if (missingInternal.length > 0) {
    throw new Error(`Missing required columns: ${missingInternal.join(', ')}`);
  }
  
  // Must have either price or value
  if (columnMap.price === -1 && columnMap.value === -1) {
    throw new Error('File must have either Price or Value column');
  }
  
  // Validate config's required columns if specified
  if (config.requiredColumns && config.requiredColumns.length > 0) {
    const normalizedHeader = headerRow.map(h => h.toLowerCase().trim());
    const missingRequired = config.requiredColumns.filter(col => {
      const normalized = col.toLowerCase().trim();
      return !normalizedHeader.some(h => h === normalized || h.includes(normalized));
    });
    
    if (missingRequired.length > 0) {
      throw new Error(`Missing required columns: ${missingRequired.join(', ')}`);
    }
  }
}

/**
 * Parse data rows into raw trades
 */
function parseRows(
  rows: string[][],
  headerRowIndex: number,
  columnMap: Record<string, number>,
  config: BrokerParserConfig
): { trades: RawTrade[]; rejectedRows: RejectedRow[] } {
  const trades: RawTrade[] = [];
  const rejectedRows: RejectedRow[] = [];
  
  const skipRows = config.headerConfig.skipRows || 0;
  const startRow = headerRowIndex + 1 + skipRows;
  
  for (let i = startRow; i < rows.length; i++) {
    const row = rows[i];
    const rowNumber = i + 1;  // 1-indexed for user display
    
    // Skip empty rows
    if (!row || row.every(cell => !cell || cell.trim() === '')) {
      continue;
    }
    
    try {
      const trade = parseRow(row, rowNumber, columnMap, config);
      if (trade) {
        trades.push(trade);
      }
    } catch (error) {
      rejectedRows.push({
        rowNumber,
        symbol: row[columnMap.symbol] || undefined,
        reason: error instanceof Error ? error.message : 'Parse error',
        rawData: row.slice(0, 8).join(', '),
      });
    }
  }
  
  return { trades, rejectedRows };
}

/**
 * Parse a single row into a RawTrade
 */
function parseRow(
  row: string[],
  rowNumber: number,
  columnMap: Record<string, number>,
  config: BrokerParserConfig
): RawTrade | null {
  const getValue = (key: string): string => {
    const idx = columnMap[key];
    if (idx === -1 || idx >= row.length) return '';
    return (row[idx] || '').trim();
  };
  
  const symbol = getValue('symbol');
  const lookupValue = getValue('lookupColumn');
  const orderId = getValue('orderId');
  const tradeTypeRaw = getValue('tradeType');
  const quantityStr = getValue('quantity');
  const exchangeRaw = getValue('exchange');
  const timestampRaw = getValue('timestamp');
  
  // Skip if missing critical fields
  if (!symbol || !orderId || !lookupValue) {
    return null;
  }
  
  // Transform trade type
  const tradeType = config.transforms.tradeType[tradeTypeRaw] || 
                    config.transforms.tradeType[tradeTypeRaw.toUpperCase()];
  if (!tradeType) {
    throw new Error(`Unknown trade type: ${tradeTypeRaw}`);
  }
  
  // Transform exchange
  let exchange: 'NSE' | 'BSE' = 'NSE';
  if (config.transforms.exchange) {
    exchange = config.transforms.exchange[exchangeRaw] || 
               config.transforms.exchange[exchangeRaw.toUpperCase()] || 'NSE';
  } else if (exchangeRaw.toUpperCase() === 'BSE' || exchangeRaw.toUpperCase() === 'B') {
    exchange = 'BSE';
  }
  
  // Parse quantity
  const quantity = parseFloat(quantityStr.replace(/,/g, ''));
  if (isNaN(quantity) || quantity <= 0) {
    throw new Error(`Invalid quantity: ${quantityStr}`);
  }
  
  // Parse price (direct or from value)
  let price = 0;
  if (columnMap.price !== -1) {
    const priceStr = getValue('price');
    price = parseFloat(priceStr.replace(/[₹,]/g, ''));
  } else if (columnMap.value !== -1) {
    const valueStr = getValue('value');
    const value = parseFloat(valueStr.replace(/[₹,]/g, ''));
    price = value / quantity;
  }
  if (isNaN(price) || price <= 0) {
    throw new Error(`Invalid price`);
  }
  
  // Parse timestamp
  const timestamp = parseTimestamp(timestampRaw, config.transforms.dateFormat);
  
  // Apply order status filter if configured
  if (config.filters?.orderStatus && columnMap.orderStatus !== -1) {
    const status = getValue('orderStatus').toLowerCase();
    const allowedStatuses = config.filters.orderStatus.map(s => s.toLowerCase());
    if (!allowedStatuses.some(s => status.includes(s))) {
      return null;  // Skip non-executed orders
    }
  }
  
  return {
    rowNumber,
    symbol,
    lookupValue,
    orderId,
    tradeType,
    quantity,
    price: Math.round(price * 100) / 100,
    exchange,
    timestamp,
  };
}

/**
 * Parse timestamp with the configured format
 */
function parseTimestamp(value: string, format: string): string {
  if (!value) {
    return new Date().toISOString();
  }
  
  // Try the configured format
  const parsed = tryParseDate(value, format);
  if (parsed) {
    return parsed.toISOString();
  }
  
  // Try native parsing as fallback
  const native = new Date(value);
  if (!isNaN(native.getTime())) {
    return native.toISOString();
  }
  
  console.warn(`[GenericParser] Could not parse date: ${value}`);
  return new Date().toISOString();
}

/**
 * Try to parse a date string with a specific format
 */
function tryParseDate(value: string, format: string): Date | null {
  // Map of month abbreviations
  const months: Record<string, number> = {
    'jan': 0, 'feb': 1, 'mar': 2, 'apr': 3, 'may': 4, 'jun': 5,
    'jul': 6, 'aug': 7, 'sep': 8, 'oct': 9, 'nov': 10, 'dec': 11,
  };
  
  try {
    if (format === 'DD-MMM-YY' || format === 'DD-MMM-YYYY') {
      // e.g., 02-Jan-26 or 02-Jan-2026
      const match = value.match(/(\d{1,2})-([A-Za-z]{3})-(\d{2,4})/);
      if (match) {
        const day = parseInt(match[1], 10);
        const month = months[match[2].toLowerCase()];
        let year = parseInt(match[3], 10);
        if (year < 100) {
          year += year > 50 ? 1900 : 2000;  // 26 -> 2026, 99 -> 1999
        }
        if (month !== undefined) {
          return new Date(year, month, day);
        }
      }
    } else if (format === 'DD/MM/YYYY') {
      const match = value.match(/(\d{1,2})\/(\d{1,2})\/(\d{4})/);
      if (match) {
        return new Date(parseInt(match[3], 10), parseInt(match[2], 10) - 1, parseInt(match[1], 10));
      }
    } else if (format === 'YYYY-MM-DD') {
      const match = value.match(/(\d{4})-(\d{1,2})-(\d{1,2})/);
      if (match) {
        return new Date(parseInt(match[1], 10), parseInt(match[2], 10) - 1, parseInt(match[3], 10));
      }
    }
  } catch (e) {
    // Ignore and return null
  }
  
  return null;
}

/**
 * Resolve ISINs via broker_symbol_mappings API
 */
async function resolveISINs(
  trades: RawTrade[],
  config: BrokerParserConfig
): Promise<{
  resolvedTrades: (RawTrade & { isin: string })[];
  unresolvedSymbols: string[];
  lookupRejectedRows: RejectedRow[];
}> {
  // Collect unique lookup values per exchange
  const lookupMap = new Map<string, RawTrade[]>();
  for (const trade of trades) {
    const key = `${trade.exchange}:${trade.lookupValue}`;
    if (!lookupMap.has(key)) {
      lookupMap.set(key, []);
    }
    lookupMap.get(key)!.push(trade);
  }
  
  // Build API request
  const lookups = Array.from(lookupMap.keys()).map(key => {
    const [exchange, value] = key.split(':');
    return { value, exchange };
  });
  
  // Call API
  const response = await resolveBrokerSymbols(
    config.broker,
    lookups,
    config.columns.isinLookup.matchField
  );
  
  if (!response.success || !response.data) {
    const errorMessage = !response.success && 'error' in response 
      ? response.error?.message 
      : 'Unknown error';
    throw new Error(`Failed to resolve symbols: ${errorMessage}`);
  }
  
  const { resolved, unresolved } = response.data;
  
  // Map resolved ISINs back to trades
  const resolvedTrades: (RawTrade & { isin: string })[] = [];
  const lookupRejectedRows: RejectedRow[] = [];
  
  for (const trade of trades) {
    const key = `${trade.exchange}:${trade.lookupValue}`;
    const isin = resolved[key];
    
    if (isin) {
      resolvedTrades.push({ ...trade, isin });
    } else {
      lookupRejectedRows.push({
        rowNumber: trade.rowNumber,
        symbol: trade.symbol,
        reason: `Symbol not found in broker mappings: ${trade.lookupValue} (${trade.exchange})`,
      });
    }
  }
  
  return {
    resolvedTrades,
    unresolvedSymbols: unresolved,
    lookupRejectedRows,
  };
}

/**
 * Aggregate trades by orderId (handle multiple rows per order)
 */
function aggregateByOrderId(trades: (RawTrade & { isin: string })[]): (RawTrade & { isin: string })[] {
  const orderMap = new Map<string, (RawTrade & { isin: string })[]>();
  
  for (const trade of trades) {
    const key = trade.orderId;
    if (!orderMap.has(key)) {
      orderMap.set(key, []);
    }
    orderMap.get(key)!.push(trade);
  }
  
  const aggregated: (RawTrade & { isin: string })[] = [];
  
  for (const [orderId, orderTrades] of orderMap.entries()) {
    if (orderTrades.length === 1) {
      aggregated.push(orderTrades[0]);
      continue;
    }
    
    // Multiple trades with same orderId - aggregate
    const first = orderTrades[0];
    let totalQty = 0;
    let totalValue = 0;
    let earliestTimestamp = first.timestamp;
    
    for (const trade of orderTrades) {
      totalQty += trade.quantity;
      totalValue += trade.quantity * trade.price;
      if (trade.timestamp < earliestTimestamp) {
        earliestTimestamp = trade.timestamp;
      }
    }
    
    const avgPrice = totalValue / totalQty;
    
    aggregated.push({
      ...first,
      quantity: totalQty,
      price: Math.round(avgPrice * 100) / 100,
      timestamp: earliestTimestamp,
    });
  }
  
  return aggregated;
}

/**
 * Group trades by ISIN into positions
 */
function groupIntoPositions(trades: (RawTrade & { isin: string })[]): ParsedPosition[] {
  const positionMap = new Map<string, ParsedTranche[]>();
  const symbolMap = new Map<string, string>();  // ISIN -> display symbol
  const exchangeMap = new Map<string, string>(); // ISIN -> exchange
  
  for (const trade of trades) {
    if (!positionMap.has(trade.isin)) {
      positionMap.set(trade.isin, []);
      symbolMap.set(trade.isin, trade.symbol);
      exchangeMap.set(trade.isin, trade.exchange);
    }
    
    positionMap.get(trade.isin)!.push({
      orderId: trade.orderId,
      symbol: trade.symbol,
      isin: trade.isin,
      type: trade.tradeType,
      quantity: trade.quantity,
      avgPrice: trade.price,
      timestamp: trade.timestamp,
      exchange: trade.exchange,
    });
  }
  
  const positions: ParsedPosition[] = [];
  
  for (const [isin, tranches] of positionMap.entries()) {
    // Sort tranches by timestamp
    tranches.sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime());
    
    positions.push({
      symbol: symbolMap.get(isin) || isin,
      isin,
      exchange: exchangeMap.get(isin) || 'NSE',
      tranches,
    });
  }
  
  // Sort positions by symbol
  positions.sort((a, b) => a.symbol.localeCompare(b.symbol));
  
  return positions;
}
